import Text from 'components/Text/Text';
import React, { useContext, useEffect, useRef } from 'react';
import { ICustomDocumentSettingsForUpdate } from 'types';
import { ALLERGEN_SETTING_KEY, TERMS_SETTING_KEY } from 'appConstants';
import { AppContext, IAppContext } from 'appState/appContext';
import { IStyleContext, StyleContext } from '../../styleState/styleContext';
import PDFUploader from 'components/UploaderAndDownloader/PDFUploader';
import BlobDownloader from 'components/UploaderAndDownloader/BlobDownloader';
import Button from 'components/Button/Button';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { Radio, RadioChangeEvent, Checkbox } from 'antd';
import { createAppError } from 'utils/helpers';
import { postMediaFile } from 'backend/additionalInformation';
import style from './DocumentSettings.module.scss';

interface IComponentDocumentSettings {
	data: ICustomDocumentSettingsForUpdate;
}

const DocumentSettings: React.FC<IComponentDocumentSettings> = ({ data }) => {
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const {
		addFileIdForDocumentSettingsForUpdate,
		setAllergenOptions,
		toggleShowModal,
		changeTypeOfModalWindow,
		selectedGroup,
	} = useContext<IStyleContext>(StyleContext);
	const headingElement = useRef<null | HTMLDivElement>(null);

	useEffect(() => {
		if (selectedGroup?.id === data.id) {
			headingElement.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [selectedGroup]);

	const handleFileLoad = async (pdfFile: File) => {
		showLoader();
		const formData = new FormData();
		formData.append('file', pdfFile, pdfFile.name);
		try {
			const response = await postMediaFile({ data: formData });
			addFileIdForDocumentSettingsForUpdate({
				documentSettingId: data.id,
				fileId: response.data.fileId,
				fileName: pdfFile.name,
				filePath: data.filePath,
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const handleDeleteFile = () => {
		addFileIdForDocumentSettingsForUpdate({
			documentSettingId: data.id,
			fileId: null,
			fileName: '',
			filePath: null,
		});
	};

	const handleSetAllergenOptions = (e: RadioChangeEvent) => {
		setAllergenOptions({ documentSettingId: data.id, allergensShowCondition: e.target.value });
	};

	const handleToggleShowModal = () => {
		toggleShowModal(data.id);
	};

	const handleSizeError = () => {
		changeTypeOfModalWindow('pdfFileSizeExceeded');
	};

	return (
		<div ref={headingElement} data-test="DocumentSettings">
			<Text
				className={style.heading}
				translationNameSpace="mediaSettings"
				translationKey={data.key}
				theme="secondary"
				size="l"
				weight="bold"
			/>
			<div className={style.contentWrapper}>
				<div className={style.content}>
					<PDFUploader
						onFileLoad={handleFileLoad}
						onSizeError={handleSizeError}
						fileName={data.fileName}
						id={data.id}
						label="uploadFile"
						maxFileSizeMB={25}
					/>
					<div className={style.downloadBtn}>
						{data.filePath ? (
							<BlobDownloader
								filePath={data.filePath}
								fileExtension="pdf"
								fileName={data.key?.toLowerCase() || ''}
								label="downloadFile"
							/>
						) : null}
					</div>
					{(!!data.filePath || !!data.fileName) && (
						<div className={style.deleteButtonWrapper}>
							<Button
								className={style.deleteButton}
								theme="secondary"
								icon={
									<svg className={style.deleteIcon}>
										<use xlinkHref={`${breadcrumbs}#bin`} />
									</svg>
								}
								onClick={handleDeleteFile}
								data-test="deleteButton">
								<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
							</Button>
						</div>
					)}
				</div>
				{data.key === ALLERGEN_SETTING_KEY && (
					<Radio.Group
						className={style.radioGroup}
						value={data.allergensShowCondition}
						disabled={!(data.filePath || data.fileId)}
						onChange={handleSetAllergenOptions}>
						<Radio value="GoingToMenu" data-test="GoingToMenu">
							<Text
								translationNameSpace="mediaSettings"
								translationKey="showWhenGoingToMenuLabel"
								size="s"
							/>
						</Radio>
						<Radio value="ChoosingDish" data-test="ChoosingDish">
							<Text
								translationNameSpace="mediaSettings"
								translationKey="showWhenChoosingDishLabel"
								size="s"
							/>
						</Radio>
					</Radio.Group>
				)}
				{data.key === TERMS_SETTING_KEY && (
					<div className={style.content}>
						<Checkbox
							checked={data.termsShowInModal || false}
							disabled={!(data.filePath || data.fileId)}
							onChange={handleToggleShowModal}>
							<Text
								translationNameSpace="mediaSettings"
								translationKey="showModalWindowLabel"
								asFragment={true}
							/>
						</Checkbox>
					</div>
				)}
			</div>
		</div>
	);
};

export default React.memo(DocumentSettings);
